import React, { useState, useEffect, useRef } from 'react';
import { Video, Download, Loader, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { FormattedMessageContent } from '@/components/chat/FormattedMessageContent';
import type { Message } from '@/types/message-types';

interface MediaVideoProps {
  message: Message;
  tenantId: string;
}

export function MediaVideo({ message, tenantId }: MediaVideoProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { toast } = useToast();
  const { t } = useTranslation('conversations');
  
  // Detect if this is a GIF based on metadata
  const isGif = message.metadata?.mime_type?.includes('gif') || 
                message.metadata?.media_type?.includes('gif');

  useEffect(() => {
    const loadVideo = async () => {
      // Debug logging for customer media issue
      console.log('🎥 MediaVideo loadVideo:', {
        messageId: message.id,
        senderType: message.sender_type,
        hasMediaUrl: !!message.media_url,
        mediaUrlValue: message.media_url,
        mediaUrlLength: message.media_url?.length,
        metadata: message.metadata,
        isGif
      });

      // First check if media_url is already available — resolve storage paths to signed URLs
      if (message.media_url?.trim()) {
        const resolved = await whatsappMediaService.resolveMediaUrl(message.media_url);
        if (resolved) {
          console.log('✅ MediaVideo: Using resolved media_url');
          setVideoUrl(resolved);
          setLoading(false);
          return;
        }
      }

      // Fallback to extracting media ID and downloading
      console.log('📥 MediaVideo: Attempting to extract media_id from metadata');
      const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
      console.log('🔍 MediaVideo: Extracted media_id:', mediaId);

      if (!mediaId) {
        console.error('❌ MediaVideo: No media ID found in message:', {
          messageId: message.id,
          metadata: message.metadata,
          media_url: message.media_url
        });
        setError('No media ID found');
        setLoading(false);
        return;
      }

      try {
        const result = await whatsappMediaService.downloadAndGetMediaUrl(
          tenantId,
          mediaId,
          'video',
          message.id
        );

        if (result.success) {
          setVideoUrl(result.fileUrl || null);
          setThumbnailUrl(result.thumbnailUrl || null);
        } else {
          setError(result.error || 'Failed to load video');
        }
      } catch (err) {
        setError('Failed to load video');
      } finally {
        setLoading(false);
      }
    };

    loadVideo();
  }, [message, tenantId, isGif]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
        <Loader className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading {isGif ? 'GIF' : 'video'}...</span>
      </div>
    );
  }

  if (error || !videoUrl) {
    return (
      <div className="flex items-center gap-2">
        <Video className="h-4 w-4" />
        <span className="text-sm">{message.content || (isGif ? '🎬 GIF' : '🎥 Video')}</span>
        {error && <span className="text-xs text-muted-foreground">({error})</span>}
      </div>
    );
  }

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleDownload = async () => {
    if (videoUrl) {
      try {
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${isGif ? 'gif' : 'video'}_${message.id}.${isGif ? 'gif' : 'mp4'}`;
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast({
          title: t('media.downloadStarted'),
          description: t('media.downloadStartedDesc', { type: isGif ? 'GIF' : 'Video' }),
        });
      } catch (error) {
        toast({
          title: t('media.downloadFailed'),
          description: t('media.downloadFailedDesc'),
          variant: "destructive",
        });
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isDragging) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleTimelineChange = (value: number[]) => {
    const newTime = value[0];
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-1 w-full max-w-xs">
      {/* Video without native controls */}
      <div className="w-full">
        <video
          ref={videoRef}
          className="w-full rounded-lg max-h-[300px] object-cover cursor-pointer"
          poster={thumbnailUrl || undefined}
          onClick={handlePlayPause}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onDurationChange={handleLoadedMetadata}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      
      {/* Timeline */}
      {duration > 0 && (
        <div className="space-y-1">
          <Slider
            value={[currentTime]}
            max={duration}
            step={0.1}
            className="w-full"
            onValueChange={handleTimelineChange}
            onPointerDown={() => setIsDragging(true)}
            onPointerUp={() => setIsDragging(false)}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      )}
      
      {/* Controls below the video */}
      <div className="flex items-center justify-between gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={handlePlayPause}
          className="h-6 px-2 text-xs gap-1"
        >
          {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          {isPlaying ? 'Pause' : 'Play'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDownload}
          className="h-6 w-6 p-0"
          title={t('media.downloadVideo')}
        >
          <Download className="h-3 w-3" />
        </Button>
      </div>
      
      {message.content && (
        <FormattedMessageContent content={message.content} className="text-sm text-muted-foreground" />
      )}
    </div>
  );
}